import { useTranslation } from 'react-i18next'

import { WarningIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { Skeleton, Text } from '@reveni-io/reveni-ui'

import {
  statisticOrientations,
  statisticSizes,
  statisticSkeletonDimensions,
  statisticStatuses,
  statisticTextTypes,
} from './constants'
import { getIconSize, getValueColor, isAlertStatus } from './helpers'
import * as Styles from './styles'

const Statistic = ({
  label,
  value,
  Icon,
  caption,
  prefix,
  suffix,
  orientation = statisticOrientations.vertical,
  size = statisticSizes.large,
  status = statisticStatuses.default,
  showStatusIcon = false,
  isAlert = false,
  isOutlined = true,
  loading = false,
  onClick,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const textTypes = statisticTextTypes[size]
  const iconSize = getIconSize(size, theme)
  const valueColor = getValueColor(status, theme)
  const isClickable = Boolean(onClick)

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    onClick()
  }

  if (loading) {
    const skeletonDimensions = statisticSkeletonDimensions[size]

    return (
      <Styles.Container orientation={orientation} size={size} isOutlined={isOutlined} aria-busy="true">
        {Icon && (
          <Skeleton variant="rounded" width={iconSize} height={iconSize} color={theme.colors.disabled.surface} />
        )}
        <Styles.Body orientation={orientation} size={size}>
          <Styles.Label orientation={orientation}>
            <Skeleton
              variant="rounded"
              width={skeletonDimensions.label.width}
              height={skeletonDimensions.label.height}
              color={theme.colors.disabled.surface}
            />
          </Styles.Label>
          <Styles.Data orientation={orientation} size={size}>
            <Skeleton
              variant="rounded"
              width={skeletonDimensions.value.width}
              height={skeletonDimensions.value.height}
              color={theme.colors.disabled.surface}
            />
            {caption && (
              <Skeleton
                variant="rounded"
                width={skeletonDimensions.caption.width}
                height={skeletonDimensions.caption.height}
                color={theme.colors.disabled.surface}
              />
            )}
          </Styles.Data>
        </Styles.Body>
      </Styles.Container>
    )
  }

  return (
    <Styles.Container
      orientation={orientation}
      size={size}
      status={status}
      isOutlined={isOutlined}
      isAlert={isAlert && isAlertStatus(status)}
      isClickable={isClickable}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      {Icon && (
        <Styles.IconContainer>
          <Icon size={iconSize} color={theme.colors.text.muted} />
        </Styles.IconContainer>
      )}
      <Styles.Body orientation={orientation} size={size}>
        <Styles.Label orientation={orientation}>
          <Text type={textTypes.label} color={theme.colors.text.subtle}>
            {label}
          </Text>
        </Styles.Label>
        <Styles.Data orientation={orientation} size={size}>
          <Styles.Value>
            {showStatusIcon && isAlertStatus(status) && (
              <WarningIcon size={iconSize} color={valueColor} alt={t('Requiere atención')} />
            )}
            <Styles.Figure>
              {prefix && (
                <Text tag="span" type={textTypes.value} color={valueColor}>
                  {prefix}
                </Text>
              )}
              <Text tag="div" type={textTypes.value} color={valueColor}>
                {value}
              </Text>
              {suffix && (
                <Text tag="span" type={textTypes.suffix} color={valueColor}>
                  {suffix}
                </Text>
              )}
            </Styles.Figure>
          </Styles.Value>
          {caption && (
            <Text type={textTypes.caption} color={theme.colors.text.muted}>
              {caption}
            </Text>
          )}
        </Styles.Data>
      </Styles.Body>
    </Styles.Container>
  )
}

export default Statistic
